import type { Timeline, TimelineLine } from './types';

/** 最后一句（或缺 end 的句子）默认持续时长 */
export const DEFAULT_LAST_LINE = 4;

/** 规范化后的句子：end 已补齐，index 为在时间轴中的序号（重复短句靠它区分）。 */
export interface ResolvedLine {
  index: number;
  start: number;
  end: number;
  text: string;
}

export function emptyTimeline(trackId?: string): Timeline {
  return { format: 'nianfo-timeline', version: 1, trackId, offset: 0, status: 'draft', lines: [] };
}

/** 按 start 排序并补齐 end。不会合并相同文字——每次出现都保留为独立一句。 */
export function resolveLines(lines: TimelineLine[]): ResolvedLine[] {
  const sorted = lines
    .map((l, i) => ({ ...l, _i: i }))
    .filter((l) => Number.isFinite(l.start))
    .sort((a, b) => a.start - b.start || a._i - b._i);
  return sorted.map((l, i) => {
    const next = sorted[i + 1];
    let end = l.end;
    if (end === undefined || !Number.isFinite(end) || end <= l.start) {
      end = next ? next.start : l.start + DEFAULT_LAST_LINE;
    }
    return { index: i, start: l.start, end, text: l.text };
  });
}

/**
 * 找到时间 t 处正在显示的句子序号；不在任何句子内返回 -1。
 * 二分查找最后一个 start <= t 的句子，再检查 t < end。
 * 若句子之间有重叠，取最近开始的一句。
 */
export function findLineIndex(lines: ResolvedLine[], t: number): number {
  let lo = 0;
  let hi = lines.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (lines[mid].start <= t) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  if (found < 0) return -1;
  return t < lines[found].end ? found : -1;
}

/* ------------------------------------------------------------------ */
/* 解析                                                                */
/* ------------------------------------------------------------------ */

export class TimelineParseError extends Error {}

/** 自动识别 JSON / LRC 文本 */
export function parseTimeline(text: string, trackId?: string): Timeline {
  const trimmed = text.trim().replace(/^﻿/, '');
  // LRC 也以「[」开头，须先于 JSON 数组判断
  if (/^\[(\d+:\d|[a-z]+:)/im.test(trimmed) && !/^\[\s*[{\]]/.test(trimmed)) return parseLrc(trimmed, trackId);
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) return parseTimelineJson(trimmed, trackId);
  throw new TimelineParseError('无法识别的时间轴格式：需要 nianfo-timeline JSON 或 LRC');
}

function toSeconds(v: unknown): number {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const s = v.trim();
    // 支持 "mm:ss.xx" / "hh:mm:ss.xx" / 纯数字
    if (/^\d+(\.\d+)?$/.test(s)) return parseFloat(s);
    const parts = s.split(':').map(Number);
    if (parts.every((n) => Number.isFinite(n))) {
      return parts.reduce((acc, n) => acc * 60 + n, 0);
    }
  }
  return NaN;
}

export function parseTimelineJson(text: string, trackId?: string): Timeline {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch (e) {
    throw new TimelineParseError('JSON 解析失败：' + (e as Error).message);
  }
  const rawLines: unknown = Array.isArray(data) ? data : (data as { lines?: unknown }).lines;
  if (!Array.isArray(rawLines)) throw new TimelineParseError('缺少 lines 数组');
  const lines: TimelineLine[] = rawLines.map((raw, i) => {
    const r = raw as Record<string, unknown>;
    const start = toSeconds(r.start ?? r.t ?? r.time);
    const endRaw = r.end ?? r.e;
    const end = endRaw === undefined || endRaw === null ? undefined : toSeconds(endRaw);
    const textVal = r.text ?? r.lyric ?? r.line;
    if (!Number.isFinite(start)) throw new TimelineParseError(`第 ${i + 1} 句缺少有效的 start`);
    if (typeof textVal !== 'string') throw new TimelineParseError(`第 ${i + 1} 句缺少 text`);
    const line: TimelineLine = { start: round3(start), text: textVal };
    if (end !== undefined && Number.isFinite(end)) line.end = round3(end);
    return line;
  });
  const obj = Array.isArray(data) ? {} : (data as Partial<Timeline>);
  return {
    format: 'nianfo-timeline',
    version: 1,
    trackId: obj.trackId ?? trackId,
    offset: typeof obj.offset === 'number' ? obj.offset : 0,
    status: obj.status ?? 'draft',
    note: obj.note,
    lines,
  };
}

/** LRC：每行可带多个时间标签；`[offset:+200]`（毫秒）会被读取。空文字行视为上一句的结束点。 */
export function parseLrc(text: string, trackId?: string): Timeline {
  const stamps: { t: number; text: string }[] = [];
  let offsetMs = 0;
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const off = line.match(/^\[offset:\s*([+-]?\d+)\s*\]$/i);
    if (off) {
      offsetMs = parseInt(off[1], 10);
      continue;
    }
    const tagRe = /\[(\d+):(\d+(?:\.\d+)?)\]/g;
    const times: number[] = [];
    let m: RegExpExecArray | null;
    let lastIndex = 0;
    while ((m = tagRe.exec(line))) {
      if (m.index !== lastIndex) break;
      times.push(parseInt(m[1], 10) * 60 + parseFloat(m[2]));
      lastIndex = tagRe.lastIndex;
    }
    if (!times.length) continue;
    const content = line.slice(lastIndex).trim();
    for (const t of times) stamps.push({ t, text: content });
  }
  stamps.sort((a, b) => a.t - b.t);
  const lines: TimelineLine[] = [];
  for (let i = 0; i < stamps.length; i++) {
    const s = stamps[i];
    if (!s.text) continue; // 空行仅作为结束标记
    const next = stamps[i + 1];
    const line: TimelineLine = { start: round3(s.t), text: s.text };
    if (next && !next.text) line.end = round3(next.t);
    lines.push(line);
  }
  // LRC 规范中 offset 为正表示歌词提前；本项目 offset 为正表示延后，故取反
  return {
    format: 'nianfo-timeline',
    version: 1,
    trackId,
    offset: -offsetMs / 1000,
    status: 'draft',
    lines,
  };
}

/* ------------------------------------------------------------------ */
/* 导出                                                                */
/* ------------------------------------------------------------------ */

export function serializeTimeline(tl: Timeline): string {
  const lines = tl.lines.map((l) => {
    const o: TimelineLine = { start: round3(l.start), text: l.text };
    if (l.end !== undefined) o.end = round3(l.end);
    return o;
  });
  const out: Timeline = {
    format: 'nianfo-timeline',
    version: 1,
    trackId: tl.trackId,
    offset: tl.offset ?? 0,
    status: tl.status ?? 'draft',
    ...(tl.note ? { note: tl.note } : {}),
    lines,
  };
  // 每句一行，方便人工阅读和 diff
  const head = JSON.stringify({ ...out, lines: undefined }, null, 2).replace(/\n}$/, '');
  const body = lines.map((l) => '    ' + JSON.stringify(l)).join(',\n');
  return `${head},\n  "lines": [\n${body}\n  ]\n}\n`;
}

function lrcStamp(t: number): string {
  const total = Math.max(0, t);
  const m = Math.floor(total / 60);
  const s = total - m * 60;
  return `[${String(m).padStart(2, '0')}:${s.toFixed(2).padStart(5, '0')}]`;
}

export function serializeLrc(tl: Timeline): string {
  const resolved = resolveLines(tl.lines);
  const out: string[] = [];
  if (tl.offset) out.push(`[offset:${Math.round(-(tl.offset ?? 0) * 1000)}]`);
  resolved.forEach((l, i) => {
    out.push(lrcStamp(l.start) + l.text);
    const next = resolved[i + 1];
    if (!next || next.start - l.end > 0.05) out.push(lrcStamp(l.end));
  });
  return out.join('\n') + '\n';
}

/* ------------------------------------------------------------------ */
/* 编辑辅助                                                            */
/* ------------------------------------------------------------------ */

/**
 * 按固定间隔生成草稿时间轴（status = draft）。
 * 只是对齐工作的起点，不是对齐结果。
 */
export function generateDraft(opts: {
  text: string;
  start: number;
  interval: number;
  duration: number;
  count: number;
  trackId?: string;
}): Timeline {
  const lines: TimelineLine[] = [];
  for (let i = 0; i < opts.count; i++) {
    const s = opts.start + i * opts.interval;
    lines.push({ start: round3(s), end: round3(s + Math.min(opts.duration, opts.interval)), text: opts.text });
  }
  return {
    format: 'nianfo-timeline',
    version: 1,
    trackId: opts.trackId,
    offset: 0,
    status: 'draft',
    note: '模板生成的草稿，需按真实音频校对',
    lines,
  };
}

/**
 * 打点：把第 index 句的开始设为 t，并把上一句的结束设为 t（若上一句结束晚于 t）。
 * 返回新的 lines（不修改原数组）。
 */
export function markLineStart(lines: TimelineLine[], index: number, t: number): TimelineLine[] {
  const out = lines.map((l) => ({ ...l }));
  if (!out[index]) return out;
  const prevEnd = out[index].end;
  const dur = prevEnd !== undefined ? prevEnd - out[index].start : undefined;
  out[index].start = round3(t);
  if (dur !== undefined && dur > 0) out[index].end = round3(t + dur);
  const prev = out[index - 1];
  if (prev && (prev.end === undefined || prev.end > t)) prev.end = round3(t);
  return out;
}

export function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
